/**
 * Follow-up chips, and the sentinel that must never reach the screen.
 *
 * Follow-ups arrive out of band on a `followups` frame. The sentinel handling
 * below is the belt to that braces: if the model's `<<<SF:FOLLOWUPS>>>` marker
 * ever leaks into the token stream, a *partial* prefix of it must be withheld
 * rather than rendered and then retracted — the investor seeing `<<<SF:F`
 * flicker under an answer about their money is worse than a token of latency.
 */

import { PRESET_QUESTIONS } from '../config/preset-questions'

/** Exactly three, per the answer contract. */
export const MAX_FOLLOW_UPS = 3

/**
 * The canonical sentinel (engine `constants.py` owns it). The second spelling
 * is the prototype's, kept only as a guard: the spelling has already drifted
 * once, and a leak of either one is a rendering bug, not a parse.
 */
export const FOLLOW_UPS_SENTINEL = '<<<SF:FOLLOWUPS>>>'
const LEGACY_FOLLOW_UPS_SENTINEL = 'FOLLOW-UPS:'
const SENTINELS = [FOLLOW_UPS_SENTINEL, LEGACY_FOLLOW_UPS_SENTINEL]

/**
 * Withholding a one-character prefix would swallow a body that legitimately
 * ends in `<` or `F`, so partial matching starts at two characters. The cost of
 * showing a lone `<` for one token is nil; the cost of eating a real character
 * off the end of a finished answer is not.
 */
const MIN_PARTIAL_SENTINEL = 2

/**
 * Lowercase, drop everything that is not alphanumeric or a space, collapse
 * whitespace. Two questions that differ only in punctuation or capitalisation
 * are the same question to a reader, and so must be to the filter.
 */
export function normaliseQuestion(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

const PRESET_QUESTION_FORMS = new Set(
  PRESET_QUESTIONS.flatMap((preset) => [
    normaliseQuestion(preset.chipLabel),
    normaliseQuestion(preset.transcriptQuestion),
  ])
)

/**
 * The chips to render: never a preset (it is already on screen), never anything
 * asked this session, never a duplicate of another chip, at most three. An
 * empty result renders nothing at all.
 */
export function selectFollowUps(
  items: readonly string[],
  askedQuestions: Iterable<string>
): string[] {
  const seen = new Set(PRESET_QUESTION_FORMS)
  for (const asked of askedQuestions) seen.add(normaliseQuestion(asked))

  const selected: string[] = []
  for (const item of items) {
    if (selected.length === MAX_FOLLOW_UPS) break
    const question = item.trim()
    const form = normaliseQuestion(question)
    if (form === '' || seen.has(form)) continue
    seen.add(form)
    selected.push(question)
  }
  return selected
}

/**
 * The part of the streamed text that is safe to render: everything before the
 * sentinel, minus any trailing fragment that could still become one.
 */
export function visibleAnswerText(text: string): string {
  let visible = text
  for (const sentinel of SENTINELS) {
    const at = visible.indexOf(sentinel)
    if (at !== -1) visible = visible.slice(0, at)
  }
  return withholdPartialSentinel(visible)
}

function withholdPartialSentinel(text: string): string {
  for (const sentinel of SENTINELS) {
    for (let length = sentinel.length - 1; length >= MIN_PARTIAL_SENTINEL; length--) {
      if (text.endsWith(sentinel.slice(0, length))) return text.slice(0, text.length - length)
    }
  }
  return text
}
